import React, { useState } from 'react';
import SettingRow from '../SettingRow/SettingRow.tsx';
import ResetButton from '../ResetButton/ResetButton.tsx';
import InfoButton from '../InfoButton/InfoButton.tsx';
import Dropdown from '../Dropdown/Dropdown.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import languageToIdMapping from "../../../../../../Utils/IdMappings/languageToIdMapping.ts";
import {
    DEFAULT_SEND_MESSAGE_PHRASE,
    DEFAULT_STOP_OUTPUT_PHRASE,
    DEFAULT_STOP_RECORDING_PHRASE
} from "../../../../../../Globals.ts";

interface SpeechCommandsSectionProps {
    sendMessagePhrase: string;
    stopOutputPhrase: string;
    stopRecordingPhrase: string;
    primaryLanguage: string;
    secondaryLanguage: string | null;
    onUpdateSettings: (settings: Record<string, any>) => void;
}

const SpeechCommandsSection: React.FC<SpeechCommandsSectionProps> = ({
    sendMessagePhrase,
    stopOutputPhrase,
    stopRecordingPhrase,
    primaryLanguage,
    secondaryLanguage,
    onUpdateSettings
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isLanguagesExpanded, setIsLanguagesExpanded] = useState(false);

    const LANGUAGES = Object.keys(languageToIdMapping);

    const handleLanguageChange = (lang: string, key: string) => {
        if (key === 'primaryLanguage') {
            const updates: Record<string, string> = { primaryLanguage: lang };

            // If the new primary language matches secondary language, clear secondary
            if (lang === secondaryLanguage) {
                updates.secondaryLanguage = '';
            }

            onUpdateSettings(updates);
        } else if (key === 'secondaryLanguage') {
            if (lang === primaryLanguage) {
                // If selected language matches primary, switch them
                onUpdateSettings({
                    primaryLanguage: lang,
                    secondaryLanguage: ''
                });
            } else {
                onUpdateSettings({ secondaryLanguage: lang });
            }
        }
    };

    const handlePhraseChange = (value: string, key: string) => {
        if (/^[a-zA-Z\s]*$/.test(value)) {
            onUpdateSettings({ [key]: value });
        }
    };

    const renderPhraseInput = (value: string, key: string, label: string, tooltip: string, placeholder: string) => (
        <SettingRow
            label={
                <div className="flex items-center space-x-1">
                    <p>{label}</p>
                    <InfoButton
                        tooltipId={`${key}-tooltip`}
                        tooltipContent={tooltip}
                    />
                </div>
            }
            content={
                <div className="flex items-center space-x-2">
                    <input
                        type="text"
                        value={value}
                        onChange={(e) => handlePhraseChange(e.target.value, key)}
                        className="bg-neutral-200 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-100 rounded-lg p-2 w-32 text-sm"
                        maxLength={25}
                        placeholder={placeholder}
                    />
                    <ResetButton onClick={() => onUpdateSettings({ [key]: placeholder })}/>
                </div>
            }
        />
    );

    return (
        <div className="space-y-4">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center space-x-2 text-sm font-medium hover:text-neutral-600 dark:hover:text-neutral-200 transition-colors duration-200 w-full"
            >
                <FontAwesomeIcon
                    icon={isExpanded ? faChevronDown : faChevronRight}
                    className="w-3 h-3"
                />
                <h3 className={`text-sm ${isExpanded ? 'font-bold' : 'font-medium'}`}>Speech Commands</h3>
            </button>

            {isExpanded && (
                <div className="space-y-4 ml-5">
                    {renderPhraseInput(
                        sendMessagePhrase,
                        'sendMessagePhrase',
                        'Send Message',
                        'The phrase to say to stop speech input and send the message.',
                        DEFAULT_SEND_MESSAGE_PHRASE
                    )}

                    {renderPhraseInput(
                        stopOutputPhrase,
                        'stopOutputPhrase',
                        'Stop Output',
                        'The phrase to say to stop the AI from generating more output.',
                        DEFAULT_STOP_OUTPUT_PHRASE
                    )}

                    {renderPhraseInput(
                        stopRecordingPhrase,
                        'stopRecordingPhrase',
                        'Stop Recording',
                        'The phrase to say to stop recording.',
                        DEFAULT_STOP_RECORDING_PHRASE
                    )}

                    <div className="space-y-4 mt-6">
                        <button
                            onClick={() => setIsLanguagesExpanded(!isLanguagesExpanded)}
                            className="flex items-center space-x-2 text-sm font-medium hover:text-neutral-600 dark:hover:text-neutral-200 transition-colors duration-200 w-full"
                        >
                            <FontAwesomeIcon
                                icon={isLanguagesExpanded ? faChevronDown : faChevronRight}
                                className="w-3 h-3"
                            />
                            <h4 className={`text-xs ${isLanguagesExpanded ? 'font-bold' : 'font-medium'}`}>Languages</h4>
                        </button>

                        {isLanguagesExpanded && (
                            <div className="space-y-4 ml-5">
                                <SettingRow
                                    label={
                                        <div className="flex items-center space-x-1">
                                            <p>Primary Language</p>
                                            <InfoButton
                                                tooltipId="primary-language-tooltip"
                                                tooltipContent="Main language for speech recognition."
                                            />
                                        </div>
                                    }
                                    content={
                                        <Dropdown
                                            options={LANGUAGES}
                                            value={primaryLanguage}
                                            onChange={(lang: string) => handleLanguageChange(lang, 'primaryLanguage')}
                                            placeholder="English (US)"
                                            allowEmpty={false}
                                        />
                                    }
                                />

                                <SettingRow
                                    label={
                                        <div className="flex items-center space-x-1">
                                            <p>Secondary Language</p>
                                            <InfoButton
                                                tooltipId="secondary-language-tooltip"
                                                tooltipContent="Additional language for speech recognition."
                                            />
                                        </div>
                                    }
                                    content={
                                        <Dropdown
                                            options={LANGUAGES.filter(lang => lang !== primaryLanguage)}
                                            value={secondaryLanguage || ''}
                                            onChange={(lang: string) => handleLanguageChange(lang, 'secondaryLanguage')}
                                            placeholder=""
                                            allowEmpty={true}
                                        />
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SpeechCommandsSection;